import { useEffect, useMemo, useState } from "react";
import { Home, LayoutDashboard, ShieldCheck, KeyRound, Download } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { readStoredTicketId } from "@/lib/storage";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISS_KEY = "jdca:pwa-install-dismissed";
const INSTALL_PROMPT_PATHS = [
  "/new",
  "/analysis/",
  "/dashboard",
  "/defense-hub/",
  "/records/",
  "/mail/",
  "/checkout/",
];

function isIosSafariLike() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua);
}

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  const standaloneNavigator = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standaloneNavigator || window.matchMedia("(display-mode: standalone)").matches;
}

function canShowInstallPromptOnPath(pathname: string) {
  return INSTALL_PROMPT_PATHS.some((path) => (path.endsWith("/") ? pathname.startsWith(path) : pathname === path));
}

export default function PwaMobileShell() {
  const location = useLocation();
  const [offline, setOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "true");
    } catch {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const activeTicketId = readStoredTicketId().trim();
  const defenseHubHref = activeTicketId ? `/defense-hub/${encodeURIComponent(activeTicketId)}` : "/dashboard";

  const mobileOnly = "md:hidden";
  const isInstallPromptPath = canShowInstallPromptOnPath(location.pathname);
  const isIosHint = isInstallPromptPath && !dismissed && !deferredPrompt && isIosSafariLike() && !isStandaloneMode();
  const canShowInstall = isInstallPromptPath && !dismissed && !!deferredPrompt;

  const shouldHideBottomNav = useMemo(() => location.pathname === "/" || location.pathname.startsWith("/admin"), [location.pathname]);

  const dismissPrompt = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // Ignore storage errors.
    }
  };

  const onInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    if (!choice || choice.outcome !== "accepted") {
      dismissPrompt();
    } else {
      setDeferredPrompt(null);
    }
  };

  return (
    <>
      {offline ? (
        <div className={`fixed left-1/2 top-16 z-50 w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 ${mobileOnly}`}>
          You&apos;re offline. Saved pages may still be available.
        </div>
      ) : null}

      {canShowInstall ? (
        <div className={`fixed bottom-20 left-1/2 z-40 box-border w-[calc(100%-1.5rem)] max-w-[calc(100vw-1.5rem)] -translate-x-1/2 rounded-xl border border-blue-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur ${mobileOnly}`}>
          <div className="flex min-w-0 items-center gap-2">
            <p className="min-w-0 flex-1 text-xs leading-snug text-slate-700">Install JusticeDrive for faster access.</p>
            <div className="flex shrink-0 items-center gap-1.5">
              <button className="rounded-md bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground" onClick={() => void onInstall()} type="button">
                <Download className="mr-1 inline h-3 w-3" />
                Install
              </button>
              <button className="rounded-md px-2 py-1 text-xs text-slate-600" onClick={dismissPrompt} type="button">Later</button>
            </div>
          </div>
        </div>
      ) : null}

      {isIosHint ? (
        <div className={`fixed bottom-20 left-1/2 z-40 w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2 rounded-xl border border-blue-200 bg-white/95 px-3 py-2 text-xs text-slate-700 shadow-sm backdrop-blur ${mobileOnly}`}>
          On iPhone: tap Share → Add to Home Screen.
          <button className="ml-2 rounded-md px-2 py-1 text-xs text-slate-600" onClick={dismissPrompt} type="button">Dismiss</button>
        </div>
      ) : null}

      {!shouldHideBottomNav ? (
        <nav className={`fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur ${mobileOnly}`}>
          <ul className="grid grid-cols-4">
            <li>
              <Link className="flex flex-col items-center gap-1 py-2 text-[11px] font-medium text-slate-700" to="/">
                <Home className="h-4 w-4" />
                Home
              </Link>
            </li>
            <li>
              <Link className="flex flex-col items-center gap-1 py-2 text-[11px] font-medium text-slate-700" to="/dashboard">
                <LayoutDashboard className="h-4 w-4" />
                My Cases
              </Link>
            </li>
            <li>
              <Link className="flex flex-col items-center gap-1 py-2 text-[11px] font-medium text-slate-700" to={defenseHubHref}>
                <ShieldCheck className="h-4 w-4" />
                Defense Hub
              </Link>
            </li>
            <li>
              <Link className="flex flex-col items-center gap-1 py-2 text-[11px] font-medium text-slate-700" to="/recover">
                <KeyRound className="h-4 w-4" />
                Recover
              </Link>
            </li>
          </ul>
        </nav>
      ) : null}
    </>
  );
}
